import type { ProfilePage } from "@amber/application/contracts";
import { EDITOR_IDENTITY_REASON } from "@amber/profiles/contracts";
import { relativeLower, type LifecycleKind } from "@amber/profiles/ui";
import { FirstPlanCard, PlanSummary, ProfileCoverage, type CoverageWindow } from "@amber/collection/ui";
import { AppLink, Button, Callout, formatDate } from "@amber/ui";
import type { ProfilePageState, ProfilePanel } from "../pageState";
import { RecheckProgress } from "./RecheckProgress";

function draftTitle(state: ProfilePageState): string {
  if (state.discovering) return "Draft: finding sources…";
  if (state.started) return "Tracking started: waiting for the scheduler";
  return "Draft: nothing is monitored yet.";
}

function draftDetail(state: ProfilePageState): string {
  if (state.discovering) return " Source discovery is drafting a first plan.";
  if (state.started) return " The plan applies at the next scheduler tick; existing monitoring keeps running.";
  return " Source discovery drafted a first plan below. Start tracking approves the pre-selected sources and applies it; it never claims earlier coverage.";
}

function coverageEmpty(p: ProfilePage, state: ProfilePageState): string | null {
  if (p.lifecycle_state === "archived")
    return "Retired: nothing is monitored for this person any more. Earlier coverage stays on each source in Sources.";
  if (p.lifecycle_state === "incidental") return "Not tracked: nothing is monitored for this person.";
  if (state.started) return "Nothing is monitored for this person until the scheduler applies the plan.";
  if (p.lifecycle_state === "draft") return "Nothing is monitored for this person yet.";
  return null;
}

function ProfileNotices({
  p,
  editor,
  editReason,
  onPanel,
  onLifecycle,
}: {
  p: ProfilePage;
  editor: boolean;
  editReason: string | null;
  onPanel: (panel: ProfilePanel) => void;
  onLifecycle: (kind: LifecycleKind) => void;
}) {
  return (
    <>
      {p.merged_from ? (
        <Callout className="mb-3" title={`${p.merged_from.name} was merged into this profile`}>
          The link you followed points to {p.merged_from.name}; its items, attributions and history now belong to{" "}
          {p.name}. The merge can be reversed from History.
        </Callout>
      ) : null}
      {p.retired ? (
        <Callout
          className="mb-3"
          title={`Retired ${formatDate(p.retired.at)} by ${p.retired.by}`}
          actions={
            <Button
              size="sm"
              disabledReason={editor ? null : EDITOR_IDENTITY_REASON}
              onClick={() => {
                onLifecycle("reactivate");
              }}
            >
              Reactivate…
            </Button>
          }
        >
          “{p.retired.reason}”. Monitoring stopped; items, attributions and history are kept and the speaker can still
          be named. The profile is read-only.
        </Callout>
      ) : null}
      {p.refresh && p.refresh.differences.length > 0 ? (
        <Callout
          tone="waiting"
          className="mb-3"
          title={`A refresh from Wikipedia proposes ${String(p.refresh.differences.length)} change${p.refresh.differences.length === 1 ? "" : "s"}`}
          actions={
            <Button
              size="sm"
              disabledReason={editReason}
              onClick={() => {
                onPanel("refresh");
              }}
            >
              Review
            </Button>
          }
        >
          Nothing changes until an Editor decides each difference.{" "}
          {p.refresh.protected.length > 0
            ? `${String(p.refresh.protected.length)} manual value${p.refresh.protected.length === 1 ? " is" : "s are"} protected and kept.`
            : ""}
        </Callout>
      ) : null}
      {p.duplicates.map((d) => (
        <Callout
          key={d.id}
          tone="waiting"
          className="mb-3"
          title={`Possible duplicate: ${d.name}`}
          actions={
            <>
              <AppLink href={`/profiles/${d.id}`}>Open it</AppLink>
              <Button
                size="sm"
                disabledReason={editReason}
                onClick={() => {
                  onPanel("merge");
                }}
              >
                Merge…
              </Button>
            </>
          }
        >
          {d.reason}.
        </Callout>
      ))}
    </>
  );
}

function TrackingNotices({
  p,
  state,
  editor,
  appearances,
  onPanel,
}: {
  p: ProfilePage;
  state: ProfilePageState;
  editor: boolean;
  appearances: number | undefined;
  onPanel: (panel: ProfilePanel) => void;
}) {
  const startButton = (
    <Button
      variant="primary"
      size="sm"
      disabledReason={state.startReason}
      onClick={() => {
        onPanel("start");
      }}
    >
      Start tracking…
    </Button>
  );
  return (
    <>
      {p.lifecycle_state === "draft" ? (
        <Callout title={draftTitle(state)} actions={!state.discovering && !state.started ? startButton : undefined}>
          Created {relativeLower(p.created_at)} by {p.created_by} from {p.created_from}.{draftDetail(state)}
        </Callout>
      ) : null}
      {p.lifecycle_state === "incidental" ? (
        <Callout title="Not tracked" actions={startButton}>
          Identified speaker with a profile; appears in {appearances ?? 0} item
          {appearances === 1 ? "" : "s"}. Nothing is monitored for this person, and nothing is missing.
        </Callout>
      ) : null}
      {p.lifecycle_state === "paused" ? (
        <Callout
          tone="waiting"
          title="Paused"
          actions={
            <Button
              size="sm"
              disabledReason={editor ? null : "Only Editors and above can pause or resume a person"}
              onClick={() => {
                onPanel("pause");
              }}
            >
              Resume…
            </Button>
          }
        >
          Shared sources keep running for others. Resuming turns the paused interval into a gap and offers catch-up.
        </Callout>
      ) : null}
    </>
  );
}

export function OverviewTab({
  p,
  state,
  editor,
  appearances,
  coverage,
  onCoverage,
  onPanel,
  onLifecycle,
}: {
  p: ProfilePage;
  state: ProfilePageState;
  editor: boolean;
  appearances: number | undefined;
  coverage: CoverageWindow;
  onCoverage: (window: CoverageWindow) => void;
  onPanel: (panel: ProfilePanel) => void;
  onLifecycle: (kind: LifecycleKind) => void;
}) {
  const { first, draftLike, started } = state;
  const open = first ? first.preselected.length + first.suggestions.length : 0;
  return (
    <>
      <ProfileNotices p={p} editor={editor} editReason={state.editReason} onPanel={onPanel} onLifecycle={onLifecycle} />
      <TrackingNotices p={p} state={state} editor={editor} appearances={appearances} onPanel={onPanel} />
      {p.recheck ? <RecheckProgress recheck={p.recheck} profileId={p.id} /> : null}
      {first && draftLike && !started ? (
        <FirstPlanCard
          profile={p}
          plan={first}
          canStart={state.startReason}
          onStart={() => {
            onPanel("start");
          }}
          onReview={() => {
            onPanel("review");
          }}
        />
      ) : null}
      <ProfileCoverage profileId={p.id} window={coverage} onWindow={onCoverage} empty={coverageEmpty(p, state)} />
      {!draftLike || started ? <PlanSummary profileId={p.id} /> : null}
      {first && !draftLike && open > 0 ? (
        <section
          aria-label="Suggested sources"
          className="mt-3 flex items-center gap-3 rounded-xl border border-divider px-4 py-3"
        >
          <span className="flex-1">
            {open === 1
              ? "1 suggested source awaits a decision."
              : `${String(open)} suggested sources await a decision.`}
          </span>
          <Button
            size="sm"
            onClick={() => {
              onPanel("review");
            }}
          >
            Review
          </Button>
        </section>
      ) : null}
    </>
  );
}
